// Uses the app's gesture-unlocked AudioContext. No media is fetched at construction.
export class EncounterAudioService {
  constructor({ getContext, isUnlocked, getVolume, fetchAudio, createAudio, storage, origin }) {
    Object.assign(this, { getContext, isUnlocked, getVolume, fetchAudio, createAudio, storage, origin });
    this.seen = new Set();
    this.active = null;
    this.storageKey = 'pfp.encounter.discovery-sounds.v1';
    try {
      const saved = JSON.parse(storage?.getItem(this.storageKey) || '[]');
      if (Array.isArray(saved)) saved.slice(-64).forEach((id) => this.seen.add(id));
    } catch { /* Storage restrictions must never block gameplay. */ }
  }

  async playDiscovery(encounter, reason) {
    const id = encounter?.id;
    if (!id || encounter.status !== 'discovered' || !['spawned', 'debug_created'].includes(reason) || this.seen.has(id)) return false;
    // Consume the discovery before asynchronous work, including a blocked play attempt.
    this.seen.add(id);
    this.seen = new Set([...this.seen].slice(-64));
    try { this.storage?.setItem(this.storageKey, JSON.stringify([...this.seen])); } catch { /* Optional persistence. */ }
    this.stop();
    let url;
    try {
      url = new URL(encounter.popupSound, this.origin);
      if (url.origin !== this.origin || !url.pathname.startsWith('/wp-content/uploads/') || !url.pathname.endsWith('.mp3')) return false;
    } catch { return false; }
    if (!this.isUnlocked()) return false;
    const context = this.getContext();
    if (context && context.state !== 'running') return false;
    const active = { id, controller: new AbortController(), audio: null, source: null, gain: null };
    this.active = active;
    try {
      if (context) {
        const response = await this.fetchAudio(url.href, {
          signal: active.controller.signal, credentials: 'omit', referrerPolicy: 'no-referrer', cache: 'default',
        });
        if (!response.ok) throw new Error('Encounter audio unavailable');
        const buffer = await context.decodeAudioData(await response.arrayBuffer());
        if (this.active !== active || context.state !== 'running') return false;
        active.source = context.createBufferSource();
        active.gain = context.createGain();
        active.source.buffer = buffer;
        active.gain.gain.value = this.getVolume();
        active.source.connect(active.gain);
        active.gain.connect(context.destination);
        active.source.onended = () => { if (this.active === active) this.stop(id); };
        active.source.start();
      } else {
        // Older browsers without Web Audio still get one caught HTMLMediaElement attempt.
        active.audio = this.createAudio();
        active.audio.preload = 'none';
        active.audio.volume = this.getVolume();
        active.audio.src = url.href;
        active.audio.onended = active.audio.onerror = () => { if (this.active === active) this.stop(id); };
        await active.audio.play();
        if (this.active !== active) return false;
      }
      return true;
    } catch {
      if (this.active === active) this.stop(id);
      return false;
    }
  }

  syncVolume() {
    if (this.active?.gain) this.active.gain.gain.value = this.getVolume();
    if (this.active?.audio) this.active.audio.volume = this.getVolume();
  }

  stop(id) {
    const active = this.active;
    if (!active || (id && id !== active.id)) return;
    this.active = null;
    active.controller.abort();
    if (active.source) {
      active.source.onended = null;
      try { active.source.stop(); } catch { /* Already ended. */ }
      active.source.disconnect();
      active.source.buffer = null;
    }
    active.gain?.disconnect();
    if (active.audio) {
      active.audio.onended = active.audio.onerror = null;
      active.audio.pause();
      active.audio.removeAttribute('src');
      active.audio.load();
    }
  }
}
